/* eslint-disable max-lines */
import tokens from '@kluein/design-tokens';
import { Box } from '@kluein/klue-ui';
import {
  useLayoutEffect,
  useState,
  useMemo,
  memo,
  useRef,
  useEffect,
} from 'react';

import useAuth from 'contexts/auth/useAuth';
import { useCompanyFeatureFlags } from 'hooks/use-company-feature-flags';
import { useResponsive } from 'hooks/use-responsive';
import { AnalyticsAction } from 'lib/analytics';
import { useGetAnswerModeSourcesQuery } from 'modules/current-compete/libs/ask-klue-answer-mode-chat/api/answer-sources.api';
import { useAnswerChat } from 'modules/current-compete/libs/ask-klue-answer-mode-chat/hooks/useAnswerChat';
import { useGetResearchModeSourcesQuery } from 'modules/current-compete/libs/ask-klue-research-mode-chat/api/research-sources.api';
import { useResearchChat } from 'modules/current-compete/libs/ask-klue-research-mode-chat/hooks/useResearchChat';
import { ChatStatusHelpers } from 'modules/shared/libs/chat/constants';
import {
  useAskKlue,
  useAskKlueRestoreSession,
  useEffectivePreviewingVisibilityGroup,
} from 'pages/dashboard/hooks';
import { useAskKlueSupportedRival } from 'pages/dashboard/hooks/use-ask-klue-supported-rival';
import {
  DASHBOARD_LOCAL_STORAGE,
  setAskKlueDialogCounter,
  setAskKlueHasFilledDefaultFeedback,
  useAskKlueHasFilledDefaultFeedback,
  useAskKlueWizardId,
  useAskKlueResearchMode,
  setAskKlueSidebarOpen,
  useAskKlueSidebarOpen,
  useAskKlueInitialScreen,
  setAskKlueInitialScreen,
  setAskKlueResearchMode,
  setAskKlueThreadId,
  useAskKlueThreadId,
  setAskKlueIsModernThread,
  setAskKluePendingQuestion,
  useAskKlueShowModal,
  useAskKluePendingQuestion,
} from 'store/views/dashboard';
import TEST_IDS from 'test-ids';

import AskKlueInput from '../ask-klue-input/AskKlueInput';
import { AskKlueModalBody } from '../ask-klue-modal-body';
import { AskKlueModalFeedback } from '../ask-klue-modal-feedback';
import { AskKlueModalFooter } from '../ask-klue-modal-footer';
import { AskKlueModalHeader } from '../ask-klue-modal-header';
import { AskKlueModalLandingScreen } from '../ask-klue-modal-landing-screen';
import { AskKlueModalSidebar } from '../ask-klue-modal-sidebar';
import { AskKluePageBanner } from '../ask-klue-page-banner/AskKluePageBanner';
import { AskKlueSingleClickPrompt } from '../ask-klue-single-click-prompt';
import { AskKlueVisibilityGroups } from '../ask-klue-visibility-groups';

import type { AskKlueEvent } from 'hooks/dashboard/use-ask-klue-log/useAskKlueLog';

type AskKlueContentProps = {
  onClose?: () => void;
  isPage?: boolean;
  onDidSubmitAskKlue?: () => void;
};

// Source types the consumer surface reverts to when
// `disableAskKlueConsumerSources` is on. Matches the original pre-modern
// consumer scope described at AskKlueCreateCardModal.tsx:404.
const ORIGINAL_CONSUMER_SOURCE_TYPES = ['CARDS', 'ALERTS'];

function getAskKlueDialogCounter() {
  return parseInt(
    localStorage.getItem(DASHBOARD_LOCAL_STORAGE.ASK_KLUE_DIALOG_COUNTER) ??
      '1',
  );
}

const AskKlueContent = ({
  onClose,
  isPage = false,
  onDidSubmitAskKlue,
}: AskKlueContentProps) => {
  const isSidebarOpen = useAskKlueSidebarOpen();
  const { visibilityGroupId, setAskKlueQueryParams } = useAskKlue();
  const { handleAskKlueInput, tryOpenSupportedRival } =
    useAskKlueSupportedRival();

  const { minWidth } = useResponsive();
  const wizardId = useAskKlueWizardId();

  const { isCurator } = useAuth();
  const previewingVisibilityGroup = useEffectivePreviewingVisibilityGroup();
  const { disableAskKlueConsumerSources } = useCompanyFeatureFlags(
    'disableAskKlueConsumerSources',
  );
  // When the FF is on, real consumers and curators previewing a visibility
  // group get the pre-modern source scope: no dropdown, sources pinned to
  // CARDS + ALERTS. Curators in Research mode are unaffected.
  const consumerSourcesLocked =
    disableAskKlueConsumerSources &&
    (!isCurator || previewingVisibilityGroup != null);
  const isResearchMode = useAskKlueResearchMode();
  const threadId = useAskKlueThreadId();
  const { isModernResearchSession, isModernAnswerSession } =
    useAskKlueRestoreSession();

  // Picks the active chat (below) and gates which sources query runs.
  const isResearchChatActive =
    !isModernAnswerSession && isResearchMode && isCurator;

  // Fetch modern research-mode sources only when the research chat is active.
  const { data: researchSourcesData, isLoading: isLoadingResearchSources } =
    useGetResearchModeSourcesQuery(undefined, {
      skip: !isResearchChatActive,
    });

  // Fetch modern answer-mode sources only when the answer chat is active.
  // Pass the active view-as visibility group so the BE returns the consumer
  // surface for a curator's preview (mirrors `checkCuratorAccess`). The
  // group is part of the query arg so RTK Query buckets the cache by
  // preview state — switching in/out of view-as refetches correctly.
  const { data: answerSourcesData, isLoading: isLoadingAnswerSources } =
    useGetAnswerModeSourcesQuery(
      { previewingVisibilityGroup: previewingVisibilityGroup ?? undefined },
      { skip: isResearchChatActive },
    );

  const modernSourcesData = isResearchChatActive
    ? researchSourcesData
    : answerSourcesData;
  const isLoadingModernSources = isResearchChatActive
    ? isLoadingResearchSources
    : isLoadingAnswerSources;

  // Local state for disabled sources (modern mode uses inverse logic - tracks disabled, not enabled)
  const [modernDisabledSources, setModernDisabledSources] = useState<string[]>(
    [],
  );

  // Reset disabled sources when switching modes or starting new chat.
  // When consumer sources are locked, disable everything BE returned except
  // the original consumer scope so the transport's `disabled_sources`
  // payload matches the pre-modern behavior.
  useEffect(() => {
    if (consumerSourcesLocked && modernSourcesData?.sources) {
      setModernDisabledSources(
        modernSourcesData.sources
          .map((s) => s.type)
          .filter((type) => !ORIGINAL_CONSUMER_SOURCE_TYPES.includes(type)),
      );
      return;
    }
    setModernDisabledSources([]);
  }, [
    isResearchMode,
    threadId,
    consumerSourcesLocked,
    modernSourcesData?.sources,
  ]);

  // Only pass threadId to the research chat when it's a modern research
  // session, so an answer thread or an old sidebar entry is never looked up
  // against /research/threads/{id}.
  const modernResearchThreadId = isModernResearchSession ? threadId : undefined;

  // Modern research chat - single instance shared between input and body
  const modernResearchChat = useResearchChat({
    threadId: modernResearchThreadId,
    disabledSources: modernDisabledSources,
  });

  // Track previous state to detect when leaving a modern research session
  const prevIsModernResearchSession = useRef(isModernResearchSession);
  // Store reset function in ref to avoid dependency on the whole modernResearchChat object
  const resetRef = useRef(modernResearchChat?.reset);
  resetRef.current = modernResearchChat?.reset;

  useEffect(() => {
    // Reset the research chat when leaving a modern research session so its
    // messages don't linger into the next chat.
    if (prevIsModernResearchSession.current && !isModernResearchSession) {
      resetRef.current?.();
    }
    prevIsModernResearchSession.current = isModernResearchSession;
  }, [isModernResearchSession]);

  // Modern Answer chat — used when starting a new chat OR when viewing a saved
  // answer-mode session.
  const answerThreadId = isModernAnswerSession ? threadId : undefined;

  const answerChat = useAnswerChat({
    threadId: answerThreadId,
    disabledSources: modernDisabledSources,
    previewingVisibilityGroup: previewingVisibilityGroup ?? undefined,
  });

  // Curators in research mode who are not viewing an answer session use the
  // research chat; everyone else uses the answer chat.
  //
  // Explicit union — answer and research chat shapes are structurally identical
  // today but typing them separately ensures any future divergence is caught
  // at compile time rather than slipping through inference.
  const activeChat: typeof answerChat | typeof modernResearchChat =
    isResearchChatActive ? modernResearchChat : answerChat;
  const showModal = useAskKlueShowModal();
  // Wait for locked consumer sources to be applied before an automatic send.
  const areSourcesReady =
    !isLoadingModernSources &&
    (!consumerSourcesLocked ||
      !modernSourcesData?.sources?.some(
        (source) =>
          !ORIGINAL_CONSUMER_SOURCE_TYPES.includes(source.type) &&
          !modernDisabledSources.includes(source.type),
      ));
  const pendingQuestion = useAskKluePendingQuestion();
  const submittedQuestionRef = useRef<typeof pendingQuestion>();

  useEffect(() => {
    // Re-arm the latch once the queue drains, so the same question can be
    // asked again later.
    if (!pendingQuestion) {
      submittedQuestionRef.current = undefined;
      return;
    }
    if (
      !areSourcesReady ||
      !(isPage ? !showModal : showModal) ||
      submittedQuestionRef.current === pendingQuestion ||
      activeChat.isLoadingHistory ||
      activeChat.hasLoadError ||
      !ChatStatusHelpers.canSubmit(activeChat.status)
    )
      return;

    // Latch this question so a re-render can't send it twice.
    // Analytics is logged at ask time in useAskKlue, not here.
    submittedQuestionRef.current = pendingQuestion;
    setAskKluePendingQuestion(undefined);
    activeChat.sendMessage(pendingQuestion);
    onDidSubmitAskKlue?.();
  }, [
    areSourcesReady,
    isPage,
    showModal,
    pendingQuestion,
    activeChat,
    onDidSubmitAskKlue,
  ]);

  // Drop an undelivered question on unmount so it can't drain into a later chat.
  useEffect(
    () => () => {
      setAskKluePendingQuestion(undefined);
    },
    [],
  );

  // Determine if any modern chat has messages (for UI visibility decisions)
  const hasActiveMessages = (activeChat?.messages?.length ?? 0) > 0;

  // Disable sends until: a question is already queued, the answer-chat
  // thread state is still being fetched, or the restore failed. Without the
  // last two gates, sends would target a transport whose setThreadState()
  // was never called.
  const isInputDisabled =
    Boolean(pendingQuestion) ||
    (activeChat?.isLoadingHistory ?? false) ||
    (activeChat?.hasLoadError ?? false);

  // Loading state for modern answer mode (only when answer chat is the active one).
  const isModernAnswerLoading =
    activeChat === answerChat &&
    ChatStatusHelpers.isProcessing(answerChat.status);

  const [showFeedbackDialog, setShowFeedbackDialog] = useState<boolean>(false);
  const hasFilledDefaultFeedback = useAskKlueHasFilledDefaultFeedback();
  const isAskKlueInitialScreen = useAskKlueInitialScreen();

  // Hide initial screen when any modern chat has messages
  // This handles the case where modern chat submits directly via sendMessage()
  // bypassing the handleAskKlue callback that normally calls setAskKlueInitialScreen(false)
  useEffect(() => {
    if (hasActiveMessages && isAskKlueInitialScreen) {
      setAskKlueInitialScreen(false);
    }
  }, [hasActiveMessages, isAskKlueInitialScreen]);
  const isAskKlueResponseLoading =
    Boolean(pendingQuestion) || isModernAnswerLoading || Boolean(wizardId);

  const handleAskKlue = (
    question: string | null,
    logEventType: AskKlueEvent['type'] = AnalyticsAction.ask,
  ) => {
    if (!question) return;

    handleAskKlueInput(question, logEventType);
    onDidSubmitAskKlue?.();
  };

  const handleNewChat = () => {
    setAskKluePendingQuestion(undefined);
    setAskKlueInitialScreen(true);
    setAskKlueResearchMode(false);
    setAskKlueThreadId(undefined);
    setAskKlueIsModernThread(false);
    // Explicitly reset modern chat state
    modernResearchChat?.reset?.();
    answerChat?.reset?.();
    setAskKlueQueryParams(
      {
        askKlueSessionId: undefined,
        askKlueCategory: undefined,
        askKlueSourceApi: undefined,
      },
      'replaceIn',
    );
  };

  const handleOnClose = () => {
    const dialogCounter = getAskKlueDialogCounter();
    const isMultipleOf5 = dialogCounter === 1 || dialogCounter % 5 === 0;

    if (isMultipleOf5 && !hasFilledDefaultFeedback && !isAskKlueInitialScreen) {
      setShowFeedbackDialog(true);
    } else {
      onClose?.();
    }
    setAskKlueHasFilledDefaultFeedback(false);
    setAskKlueDialogCounter();
  };

  // Track previous minWidth.large to only update sidebar on actual screen size changes
  const prevMinWidthLarge = useRef<boolean | undefined>(undefined);

  useLayoutEffect(() => {
    const shouldBeOpen = minWidth.large && !isPage;

    // Update sidebar on initial mount OR when screen size actually changes
    // This prevents closing the sidebar during state changes (like switching sessions)
    // while still responding to browser resize
    if (
      prevMinWidthLarge.current === undefined ||
      prevMinWidthLarge.current !== minWidth.large
    ) {
      setAskKlueSidebarOpen(shouldBeOpen);
      prevMinWidthLarge.current = minWidth.large;
    }
  }, [minWidth.large, isPage]);

  const boxStyles = useMemo(
    () =>
      isPage
        ? {
            width: 'clamp(320px, 100%, 1000px)',
            height: '100%',
            margin: { horizontal: 'auto' },
            // Don't set overflow here - let AskKlueModalBody handle scrolling internally
            // so the input stays pinned to the bottom
          }
        : { width: '100%' },
    [isPage],
  );

  const content = (
    <Box
      className={isPage ? 'ask-klue-page-content' : ''}
      fill={!isPage}
      background={`linear-gradient(180deg, ${tokens.color.neutral.white.main} 0%, #FCFCFF 53.55%, ${tokens.color.primary.light} 100%)`}
      round={isPage ? undefined : '5px'}
    >
      <AskKlueModalHeader
        onClose={isPage ? undefined : handleOnClose}
        isResponseLoading={isAskKlueResponseLoading}
        toggleSidebar={() => {
          setAskKlueSidebarOpen(!isSidebarOpen);
        }}
        handleNewChat={handleNewChat}
        isPage={isPage}
      />
      <Box direction={isSidebarOpen ? 'row' : 'column'}>
        {isSidebarOpen && <AskKlueModalSidebar />}
        <Box
          data-test-id={TEST_IDS.askKlue.answerModal.container}
          pad={{
            horizontal: tokens.spacing.large,
          }}
          direction="column"
          overflow="hidden"
          {...boxStyles}
        >
          {!hasActiveMessages && <AskKluePageBanner isPage={isPage} />}
          {isAskKlueInitialScreen && !isPage ? (
            <AskKlueModalLandingScreen
              handleAskKlue={(question: string) => {
                handleAskKlue(question);
                setAskKlueInitialScreen(false);
              }}
              activeChat={activeChat}
              isResearchMode={isResearchMode}
              modernSources={modernSourcesData?.sources}
              modernDisabledSources={modernDisabledSources}
              onModernDisabledSourcesChange={setModernDisabledSources}
              isLoadingModernSources={isLoadingModernSources}
              consumerSourcesLocked={consumerSourcesLocked}
            />
          ) : (
            <>
              <AskKlueModalBody
                pendingQuestion={pendingQuestion}
                isPage={isPage}
                activeChat={activeChat}
                isResearchChatActive={isResearchChatActive}
                modernSources={modernSourcesData?.sources}
                modernDisabledSources={modernDisabledSources}
                onDidSubmitAskKlue={onDidSubmitAskKlue}
              />
              <Box
                flex={{ shrink: 0 }}
                margin={{ bottom: 'large', top: 'medium' }}
                width={{ min: 'fit-content' }}
              >
                <AskKlueInput
                  handleAskKlue={handleAskKlue}
                  tryOpenSupportedRival={tryOpenSupportedRival}
                  isModal={!isPage}
                  isPage={isPage}
                  isResearchModeClickable={
                    isPage && isCurator && !hasActiveMessages
                  }
                  isResponseLoading={isAskKlueResponseLoading}
                  activeChat={activeChat}
                  disabled={isInputDisabled}
                  modernSources={modernSourcesData?.sources}
                  modernDisabledSources={modernDisabledSources}
                  onModernDisabledSourcesChange={setModernDisabledSources}
                  isLoadingModernSources={isLoadingModernSources}
                  consumerSourcesLocked={consumerSourcesLocked}
                  onDidSubmitAskKlue={onDidSubmitAskKlue}
                />
                {isPage && !hasActiveMessages && (
                  <Box>
                    <AskKlueSingleClickPrompt
                      isPage={true}
                      handleAskKlue={handleAskKlue}
                      isResponseLoading={isAskKlueResponseLoading}
                      activeChat={activeChat}
                      isResearchMode={isResearchMode}
                      disabled={isInputDisabled}
                      onDidSubmitAskKlue={onDidSubmitAskKlue}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
          <AskKlueModalFooter
            isPage={isPage}
            feedbackEnabled={true}
            isInitialScreen={isAskKlueInitialScreen}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {isCurator && !isResearchMode && !isPage ? (
        <AskKlueVisibilityGroups visibilityGroupId={visibilityGroupId} />
      ) : null}

      <AskKlueModalFeedback
        showDialog={showFeedbackDialog && !isAskKlueInitialScreen}
        onCloseDialog={() => {
          setShowFeedbackDialog(false);
          onClose?.();
        }}
      >
        {content}
      </AskKlueModalFeedback>
    </>
  );
};

export default memo(AskKlueContent);
